// d:/Hospinet/opensky-digital-twins/packages/rc-app/src/components/LogoUploadWithCrop/index.tsx

import React, { useState, useRef } from "react";
import Icon from "../Icons";

interface LogoUploadWithCropProps {
  value?: string;
  onChange: (url: string) => void;
  onUpload: (file: File) => Promise<string>;
  maxWidth?: number;
  maxHeight?: number;
  isUploading?: boolean;
  builderName?: string;
}

const LogoUploadWithCrop: React.FC<LogoUploadWithCropProps> = ({
  value,
  onChange,
  onUpload,
  maxWidth = 400,
  maxHeight = 400,
  isUploading = false,
  builderName = "",
}) => {
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [originalFileName, setOriginalFileName] = useState<string>("");
  const [cropArea, setCropArea] = useState({
    x: 0,
    y: 0,
    width: 200,
    height: 200,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setOriginalFileName(file.name);

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        if (img.width > maxWidth || img.height > maxHeight) {
          setImageToCrop(event.target?.result as string);
          setShowCropModal(true);
        } else {
          uploadImage(file);
        }
      };
      img.src = event.target?.result as string;
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const uploadImage = async (file: File) => {
    try {
      const url = await onUpload(file);
      onChange(url);
    } catch (error) {
      console.error("Upload failed:", error);
    }
  };

  const handleCrop = async () => {
    if (!canvasRef.current || !imageRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = maxWidth;
    canvas.height = maxHeight;

    const scaleX = imageRef.current.naturalWidth / imageRef.current.width;
    const scaleY = imageRef.current.naturalHeight / imageRef.current.height;

    ctx.drawImage(
      imageRef.current,
      cropArea.x * scaleX,
      cropArea.y * scaleY,
      cropArea.width * scaleX,
      cropArea.height * scaleY,
      0,
      0,
      maxWidth,
      maxHeight,
    );

    canvas.toBlob(async (blob) => {
      if (blob) {
        const timestamp = Date.now();
        const file = new File([blob], `${timestamp}_${originalFileName}`, {
          type: "image/png",
        });
        await uploadImage(file);
        setShowCropModal(false);
        setImageToCrop(null);
      }
    }, "image/png");
  };

  const handleCropImageLoad = () => {
    if (!imageRef.current) return;
    const displayedWidth = imageRef.current.width;
    const displayedHeight = imageRef.current.height;
    const size = Math.min(displayedWidth, displayedHeight, 150);
    setCropArea({
      x: (displayedWidth - size) / 2,
      y: (displayedHeight - size) / 2,
      width: size,
      height: size,
    });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - cropArea.x, y: e.clientY - cropArea.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !imageRef.current) return;

    const newX = Math.max(
      0,
      Math.min(
        e.clientX - dragStart.x,
        imageRef.current.width - cropArea.width,
      ),
    );
    const newY = Math.max(
      0,
      Math.min(
        e.clientY - dragStart.y,
        imageRef.current.height - cropArea.height,
      ),
    );

    setCropArea({ ...cropArea, x: newX, y: newY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <>
      <div className="flex flex-col items-center">
        <div className="relative mb-3">
          {!value ? (
            builderName && builderName.length >= 2 ? (
              <div className="w-28 h-28 md:w-32 md:h-32 rounded-full flex items-center justify-center bg-[#2E4051] text-[#889DB2] text-3xl md:text-4xl font-bold border-4 border-[#889DB2] shadow-md">
                {`${builderName[0]?.toUpperCase()}${builderName[1]?.toUpperCase()}`}
              </div>
            ) : (
              <div className="w-28 h-28 md:w-32 md:h-32 rounded-full flex items-center justify-center bg-[#2E4051] text-[#889DB2] border-4 border-[#889DB2] shadow-md">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-12 w-12"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
            )
          ) : (
            <div className="w-28 h-28 md:w-32 md:h-32 rounded-full overflow-hidden border-4 border-[#889DB2] shadow-md">
              <img
                src={value}
                alt="Logo"
                className="w-full h-full object-cover"
              />
            </div>
          )}
        </div>

        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileSelect}
        />

        <div className="flex space-x-4">
          {!value && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="bg-[#008564] text-white p-3 rounded-lg hover:opacity-70 transition-colors disabled:opacity-50 shadow-xl"
              title="Upload New Image"
            >
              {isUploading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
              )}
            </button>
          )}

          {value && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="bg-[#008564] text-white p-3 rounded-lg hover:opacity-70 transition-colors disabled:opacity-50 shadow-xl"
              title="Edit Image"
            >
              <Icon name="ic-edit-img" className="w-4 h-4" />
            </button>
          )}

          <button
            type="button"
            onClick={() => onChange("")}
            disabled={isUploading || !value}
            className="bg-[#2E4051] text-white p-3 rounded-lg hover:opacity-70 transition-colors disabled:opacity-50 shadow-xl"
            title="Delete Photo"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>
      </div>

      {showCropModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-[#1C2C3A] rounded-lg p-4 sm:p-6 max-w-[95vw] sm:max-w-xl md:max-w-3xl w-full max-h-[90vh] overflow-auto">
            <h3 className="text-base sm:text-lg md:text-xl font-semibold text-white mb-2 sm:mb-4">Crop Logo</h3>
            <p className="text-[#889DB2] text-xs sm:text-sm md:text-base mb-3 sm:mb-4">
              Image size exceeds {maxWidth}x{maxHeight}px. Drag the box to
              select the area to keep.
            </p>

            <div className="flex justify-center mb-3 sm:mb-4">
              <div
                className="relative inline-block"
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                <img
                  ref={imageRef}
                  src={imageToCrop || ""}
                  alt="Crop preview"
                  className="max-w-full max-h-52 sm:max-h-72 md:max-h-96"
                  draggable={false}
                  onLoad={handleCropImageLoad}
                />
                <div
                  className="absolute border-4 border-[#008564] cursor-move shadow-lg"
                  style={{
                    left: cropArea.x,
                    top: cropArea.y,
                    width: cropArea.width,
                    height: cropArea.height,
                    boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.5)",
                  }}
                  onMouseDown={handleMouseDown}
                >
                  <div className="absolute inset-0 border-2 border-white opacity-50"></div>
                </div>
              </div>
            </div>

            <canvas ref={canvasRef} className="hidden" />

            <div className="flex justify-end space-x-3 sm:space-x-4">
              <button
                type="button"
                onClick={() => {
                  setShowCropModal(false);
                  setImageToCrop(null);
                }}
                className="px-3 sm:px-4 py-2 text-sm sm:text-base bg-[#2E4051] text-white rounded-lg hover:opacity-70"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCrop}
                className="px-3 sm:px-4 py-2 text-sm sm:text-base bg-[#008564] text-white rounded-lg hover:opacity-70"
              >
                Crop & Upload
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LogoUploadWithCrop;
